import React, { useState, useContext, useEffect } from "react";
import DOMPurify from "dompurify";
import {
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  CardFooter,
} from "reactstrap";
import Select from "react-select";
import AdminHeader from "components/Headers/AdminHeader";
import { fixedWidthCustomStyle, PREVIEW_LIMIT } from "constants/CommonConstants";
import { useNavigate, useParams } from "react-router-dom";
import {
  DEFAULT_RECORDS_PER_PAGE,
  ANSWER_TYPES,
  LANGUAGE_OPTIONS,
  QUESTION_LANGUAGE_OPTIONS,
} from "components/constant";
import CustomPagination from "components/CommonComponents/ReactTable/CustomPagination";
import {
  showAlert,
  toTitleCase,
} from "components/CommonComponents/CommonFunctions";

import "react-quill/dist/quill.snow.css";
import AddQuestion from "pages/Courses/AddQuestion";
import ExistingQuestionModal from "./ExistingQuestionModal";

import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import {
  getQuestionBankById,
  updateQuestionBank,
  getQuestionBankDifficultyList,
} from "action/questionBank";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import { deleteQuestion } from "action/question";
import { useDispatch, useSelector } from "react-redux";
import RightPanelCard from "components/CommonComponents/RightPanelCard";
import DetailsHeader from "components/CommonComponents/DetailsHeader";



const QuestionBank = () => {

  const dispatch = useDispatch();
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const { notify } = useContext(NotificationContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const decodedId = id ? urlDecodedId(id) : null;
  const [searchState, setSearchState] = useState({
    question: "",
    ansType: null,
    level: null,
    language: null,
    filterParams: [],
  });
  const [pageNum, setPageNum] = useState(1);
  const [questionBankInfo, setQuestionBankInfo] = useState({
    questionBankName: "",
    language: "",
    tag: "",
    gradingType: "",
  });
  const [qbQuestions, setQbQuestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [currentQB, setCurrentQB] = useState(null);
  const [uiState, setUiState] = useState({
    qbPanel: {
      isOpen: false,
      mode: "ADD",
    },
    existingModal: false,
    questionBankPanel: false,
  });
  const [loadingState, setLoadingState] = useState({
    table: false,
    page: false,
    search: false,
    noRecords: false,
  });
  const [editQuestionBank, setEditQuestionBank] = useState({
    name: {
      value: "",
      error: "",
    },
    status: {
      value: "ACTIVE",
      error: "",
    },
  });
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedOptions, setExpandedOptions] = useState({});
  const [previewData, setPreviewData] = useState({
    src: "",
    width: 0,
    height: 0,
  });
  const questionBankDifficultyData = useSelector(
    (state) => state.questionBanks.questionDifficultyList,
  );

  useEffect(() => {
    getQuestionBankDifficultyList(dispatch);
  }, [dispatch]);

  useEffect(() => {
    fetchQBData();
  }, [decodedId, pageNum, searchState.filterParams]);

  const fetchQBData = async () => {
    if (decodedId) {
      setLoadingState((prev) => ({
        ...prev,
        ...(prev.search || prev.table ? { table: true } : { page: true }),
      }));
      try {
        const qbResponse = await getQuestionBankById(
          decodedId,
          pageNum,
          searchState.filterParams,
        );

        if (qbResponse?.code === 200) {
          const languageLabel =
            LANGUAGE_OPTIONS.find(
              (item) => item.value === qbResponse.data.language,
            )?.label || qbResponse.data.language;

          setQuestionBankInfo({
            id: qbResponse.data.ID,
            questionBankName: qbResponse.data.bankName,
            language: languageLabel,
            tag: qbResponse.data.tagName,
            status: qbResponse.data.status,
            totalQuestions: qbResponse.data.totalQuestions,
            totalFilteredQuestions: qbResponse.data.totalFilteredQuestions,
            noOfRecordsPerPage: qbResponse.data.noOfRecordsPerPage,
          });

          const questions =
            qbResponse.data.Question?.map((q) => ({
              id: q.ID,
              language: q.language,
              level: q.level,
              question: {
                english: q.language === "ENGLISH" ? q.question : "",
                hindi: q.language === "HINDI" ? q.question : "",
              },
              options:
                q.Answer?.map((ans) => ({
                  textEnglish: q.language === "ENGLISH" ? ans.answer : "",
                  textHindi: q.language === "HINDI" ? ans.answer : "",
                  grade: ans.grade / 100,
                  isCorrect: ans.grade > 0,
                })) || [],
              answer:
                q.Answer?.map((ans, idx) => (ans.grade > 0 ? idx : -1)).filter(
                  (idx) => idx !== -1,
                ) || [],
              type: q.answerType,
              marks: q.marks,
              status: q.status,
            })) || [];
          setQbQuestions(questions);
          setLoadingState((prev) => ({
            ...prev,
            noRecords: questions.length === 0,
          }));
        } else {
          const message = Array.isArray(qbResponse?.message)
            ? qbResponse.message
                .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
            : qbResponse?.message || "Failed to load question bank details";

          notify("danger", message);
        }
      } catch (error) {
        notify(
          "danger",
          error?.message ||
            "Something went wrong while fetching question bank details",
        );
      } finally {
        setLoadingState((prev) => ({
          ...prev,
          page: false,
          table: false,
          search: false,
        }));
      }
    }
  };

  const getPlainText = (html) => {
    if (!html) return "";
    const el = document.createElement("div");
    el.innerHTML = html;
    return el.textContent || "";
  };

  const truncateHtml = (html, limit) => {
    if (!html) return "";
    const container = document.createElement("div");
    container.innerHTML = html;
    let count = 0;
    const walk = (node) => {
      if (count >= limit) {
        node.parentNode?.removeChild(node);
        return;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        const remaining = limit - count;
        if (node.textContent.length > remaining) {
          node.textContent = node.textContent.slice(0, remaining) + "...";
          count = limit;
        } else {
          count += node.textContent.length;
        }
      } else {
        Array.from(node.childNodes).forEach(walk);
      }
    };
    Array.from(container.childNodes).forEach(walk);
    return container.innerHTML;
  };

  const levelOptions =
    questionBankDifficultyData?.data?.QuestionDifficultyLevel?.map((item) => ({
      value: item.ID,
      label:
        item.levelName.charAt(0).toUpperCase() +
        item.levelName.slice(1).toLowerCase(),
    })) || [];

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    switch (name) {
      case "questionBankName":
        if (!trimmedValue) return "Please enter question bank name";
        return "";

      default:
        return "";
    }
  };

  const handleDelete = (questionId) => {
    showAlert({
      title: "Are you sure you want to delete this question?",
      message:
        "Deleting this question from the question bank will not remove it from assessments.",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setLoadingState((prev) => ({
            ...prev,
            table: true,
          }));

          const res = await deleteQuestion(questionId);

          if (res?.code === 200) {
            notify("success", res?.message || "Question deleted successfully");

            await fetchQBData();
          } else {
            notify("danger", res?.message || "Failed to delete question");
          }
        } catch (error) {
          notify("danger", error?.message || "Something went wrong!");
        } finally {
          setLoadingState((prev) => ({
            ...prev,
            table: false,
          }));
        }
      },
    });
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (searchState.question?.trim()) {
      filterParams.push({
        field: "Question",
        condition: "like",
        filterValues: [searchState.question.trim()],
      });
    }

    if (searchState.level?.value) {
      filterParams.push({
        field: "Level",
        condition: "eq",
        filterValues: [searchState.level.label],
      });
    }

    if (searchState.language?.value) {
      filterParams.push({
        field: "Language",
        condition: "eq",
        filterValues: [searchState.language.value],
      });
    }

    if (searchState.ansType?.value) {
      filterParams.push({
        field: "AnswerType",
        condition: "eq",
        filterValues: [searchState.ansType.value],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setLoadingState((prev) => ({
        ...prev,
        search: true,
        table: true,
      }));

      setSearchState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const clearSearch = () => {
    setLoadingState((prev) => ({
      ...prev,
      table: true,
    }));

    setSearchState({
      question: "",
      ansType: null,
      level: null,
      language: null,
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleUpdateQuestionBank = async () => {
    if (!validateForm()) return;
    const hasChanges =
      editQuestionBank.name.value !== questionBankInfo.questionBankName ||
      editQuestionBank.status.value !== questionBankInfo.status;

    if (!hasChanges) {
      notify("info", "No changes detected");
      return;
    }

    const payload = {
      name: editQuestionBank.name.value,
      status: editQuestionBank.status.value,
    };

    try {
      setUiState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      const res = await updateQuestionBank(questionBankInfo.id, payload);

      if (res?.code === 200) {
        notify("success", res?.message || "Question bank updated successfully");

        setUiState((prev) => ({
          ...prev,
          questionBankPanel: false,
        }));

        await fetchQBData();
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to update question bank";

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Failed to update question bank");
    } finally {
      setUiState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const handleEditQuestionBank = () => {
    setEditQuestionBank({
      name: {
        value: questionBankInfo.questionBankName,
        error: "",
      },
      status: {
        value: questionBankInfo.status,
        error: "",
      },
    });

    setUiState((prev) => ({
      ...prev,
      questionBankPanel: true,
    }));
  };

  const handleInputChange = (field, value) => {
    setEditQuestionBank((prev) => ({
      ...prev,
      [field]: {
        value,
        error: "",
      },
    }));
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    const error = getValidationError(
      name === "name" ? "questionBankName" : name,
      trimmedValue,
    );

    setEditQuestionBank((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateForm = () => {
    let updatedForm = { ...editQuestionBank };

    Object.keys(updatedForm).forEach((key) => {
      updatedForm[key] = {
        ...updatedForm[key],
        error: getValidationError(
          key === "name" ? "questionBankName" : key,
          updatedForm[key].value,
        ),
      };
    });

    setEditQuestionBank(updatedForm);

    return Object.values(updatedForm).every((field) => !field.error);
  };

  const handleImagePreview = (e) => {
    const link = e.target.closest("a");
    if (link) {
      e.preventDefault();
    }

    const img = e.target.closest("img");
    if (!img) return;

    e.preventDefault();
    e.stopPropagation();
    setPreviewData({
      src: img.src,
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  };

  const renderQuestionCard = (q, index) => {
    const questionText = q.question?.english || q.question?.hindi || "";
    const plainText = getPlainText(questionText);

    const isExpanded = expandedQuestions[index];
    const shouldTrim = plainText.length > PREVIEW_LIMIT;
    const displayText =
      isExpanded || !shouldTrim
        ? questionText
        : truncateHtml(questionText, PREVIEW_LIMIT);

    const questionLevelLabel =
      levelOptions.find((item) => item.value === q.level)?.label ||
      toTitleCase(q.level || "");

    return (
      <Card key={q.id} className="section-header mb-4">
        <CardBody>
          <div className="d-flex justify-content-between align-items-start mb-4 question-header">
            <div className="d-flex flex-grow-1">
              <div className="question-number-box">Que {((pageNum - 1) * (questionBankInfo.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE)) + index + 1}.</div>

              <div className="question-content ml-3 flex-grow-1">
                <>
                  <div
                    className="question-html-content small-text"
                    onClick={handleImagePreview}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(displayText),
                    }}
                  />

                  {shouldTrim && (
                    <div className="mt-1">
                      <button
                        className="lesson-read-more-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedQuestions((prev) => ({
                            ...prev,
                            [index]: !prev[index],
                          }));
                        }}
                      >
                        {isExpanded ? "Read Less" : "Read More"}
                      </button>
                    </div>
                  )}
                </>
              </div>
            </div>

            <div className="question-meta">
              <span className="question-type-badge">
                {ANSWER_TYPES.find((item) => item.value === q.type)?.label ||
                  q.type}
              </span>

              <span className="question-mark-badge">
                Mark: {String(q.marks).padStart(2, "0")}
              </span>

              <span className="question-mark-badge">
                Level: {questionLevelLabel}
              </span>

              <Button
                color="link"
                size="lg"
                className="d-flex align-items-center p-0 ml-1"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(q.id);
                }}
              >
                <i className="fas fa-trash mx-1 text-danger mr-1" />
              </Button>
            </div>
          </div>

          {(q.type === "SINGLE_CHOICE" || q.type === "MULTIPLE_CHOICE") && (
            <Row>
              {q.options.map((opt, i) => {
                const isCorrect = q.answer?.includes(i);

                return (
                  <Col md="6" key={i} className="mb-3">
                    <div
                      className={`question-option-card${
                        isCorrect ? " option-correct" : " option-disabled"
                      }`}
                    >
                      <div className="d-flex align-items-center w-100">
                        <div
                          className={`option-icon mr-3${isCorrect ? " text-success" : " text-muted"}`}
                        >
                          {q.type === "SINGLE_CHOICE" ? (
                            <i
                              className={`fa-${
                                isCorrect
                                  ? "solid fa-circle-check text-success"
                                  : "regular fa-circle text-muted"
                              }`}
                            />
                          ) : (
                            <i
                              className={`fa-${
                                isCorrect
                                  ? "solid fa-square-check text-success"
                                  : "regular fa-square text-muted"
                              }`}
                            />
                          )}
                        </div>

                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center w-100">
                            <div className="flex-grow-1">
                              {(() => {
                                const optText = opt.textEnglish || opt.textHindi || "";
                                const plainOptText = getPlainText(optText);
                                const optKey = `${index}-${i}`;
                                const isOptExpanded = expandedOptions[optKey];
                                const shouldTrimOpt = plainOptText.length > PREVIEW_LIMIT;
                                const displayOptText = isOptExpanded || !shouldTrimOpt
                                  ? optText
                                  : truncateHtml(optText, PREVIEW_LIMIT);

                                return (
                                  <>
                                    <div
                                      className="question-html-content flex-grow-1"
                                      onClick={handleImagePreview}
                                      dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(displayOptText),
                                      }}
                                    />
                                    {shouldTrimOpt && (
                                      <div className="mt-1">
                                        <button
                                          className="lesson-read-more-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedOptions((prev) => ({
                                              ...prev,
                                              [optKey]: !prev[optKey],
                                            }));
                                          }}
                                        >
                                          {isOptExpanded ? "Read Less" : "Read More"}
                                        </button>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>

                            {opt.grade !== undefined && opt.grade !== null && (
                              <span className="option-grade ml-2">
                                ({Math.round(opt.grade * 100)}%)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}

          {q.type === "TRUE_FALSE" && (
            <Row>
              {q.options.map((opt, i) => {
                const isCorrect = q.answer?.[0] === i;

                return (
                  <Col md="6" key={i} className="mb-3">
                    <div
                      className={`question-option-card${
                        isCorrect ? " option-correct" : " option-disabled"
                      }`}
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className={`option-icon mr-3${isCorrect ? " text-success" : " text-muted"}`}
                        >
                          <i
                            className={`fa-${
                              isCorrect
                                ? "solid fa-circle-check text-success"
                                : "regular fa-circle text-muted"
                            }`}
                          />
                        </div>

                        <div className="flex-grow-1">
                          {(() => {
                            const optText = opt.textEnglish || opt.textHindi || "";
                            const plainOptText = getPlainText(optText);
                            const optKey = `${index}-${i}`;
                            const isOptExpanded = expandedOptions[optKey];
                            const shouldTrimOpt = plainOptText.length > PREVIEW_LIMIT;
                            const displayOptText = isOptExpanded || !shouldTrimOpt
                              ? optText
                              : truncateHtml(optText, PREVIEW_LIMIT);

                            return (
                              <>
                                <div
                                  className="question-html-content flex-grow-1"
                                  onClick={handleImagePreview}
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(displayOptText),
                                  }}
                                />
                                {shouldTrimOpt && (
                                  <div className="mt-1">
                                    <button
                                      className="lesson-read-more-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedOptions((prev) => ({
                                          ...prev,
                                          [optKey]: !prev[optKey],
                                        }));
                                      }}
                                    >
                                      {isOptExpanded ? "Read Less" : "Read More"}
                                    </button>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}

          {q.type === "IMAGE_CHOICE" && (
            <Row className="mt-3">
              {q.options.map((opt, i) => {
                const isCorrect = q.answer?.includes(i);

                return (
                  <Col lg="3" md="4" sm="6" xs="12" key={i} className="mb-3">
                    <div
                      className={`image-option-card${
                        isCorrect ? " option-correct" : " option-disabled"
                      }`}
                    >
                      {opt.image && (
                        <div className="image-wrapper mb-2">
                          <img
                            src={opt.image}
                            alt={`Option ${i + 1}`}
                            className="img-fluid rounded"
                          />
                        </div>
                      )}

                      <div className="d-flex align-items-center">
                        <div
                          className={`option-icon mr-2${isCorrect ? " text-success" : " text-muted"}`}
                        >
                          <i
                            className={`fa-${
                              isCorrect
                                ? "solid fa-square-check text-success"
                                : "regular fa-square text-muted"
                            }`}
                          />
                        </div>

                        <div className="flex-grow-1">
                          {(() => {
                            const optText = opt.textEnglish || opt.textHindi || "";
                            const plainOptText = getPlainText(optText);
                            const optKey = `${index}-${i}`;
                            const isOptExpanded = expandedOptions[optKey];
                            const shouldTrimOpt = plainOptText.length > PREVIEW_LIMIT;
                            const displayOptText = isOptExpanded || !shouldTrimOpt
                              ? optText
                              : truncateHtml(optText, PREVIEW_LIMIT);

                            return (
                              <>
                                <div
                                  className="question-html-content flex-grow-1"
                                  onClick={handleImagePreview}
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(displayOptText),
                                  }}
                                />
                                {shouldTrimOpt && (
                                  <div className="mt-1">
                                    <button
                                      className="lesson-read-more-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedOptions((prev) => ({
                                          ...prev,
                                          [optKey]: !prev[optKey],
                                        }));
                                      }}
                                    >
                                      {isOptExpanded ? "Read Less" : "Read More"}
                                    </button>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </CardBody>
      </Card>
    );
  };

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6 course-layout">
        <Row>
          <Col md="12">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center text-white ml-1">
                <span className="font-weight-bold small-text">
                  Question Bank Details
                </span>
              </div>
            </div>
            <DetailsHeader
              onBack={() =>
                navigate(
                  role === "SUPER_ADMIN"
                    ? "/super-admin/question-bank"
                    : "/admin/question-bank",
                )
              }
              backTooltip="Back to Question Banks"
              title={
                loadingState.page
                  ? "Question Bank Details"
                  : questionBankInfo.questionBankName || "Question Bank Details"
              }
              status={loadingState.page ? "" : questionBankInfo.status}
              onEdit={handleEditQuestionBank}
              editLabel="Edit"
              editTooltip="Edit Question Bank"
              editIcon="fa fa-pencil"
              showEdit={!loadingState.page}
              chips={
                loadingState.page
                  ? []
                  : [
                      {
                        id: "qbLanguage",
                        icon: "fas fa-language",
                        name: "Language",
                        value: questionBankInfo.language,
                        tooltip: `Language: ${questionBankInfo.language}`,
                      },
                      {
                        id: "qbTag",
                        icon: "fas fa-tag",
                        name: "Tag",
                        value: questionBankInfo.tag,
                        tooltip: `Tag: ${questionBankInfo.tag}`,
                      },
                    ]
              }
            />
          </Col>
        </Row>

        {loadingState.page ? (
          <Row>
            <Col md="12">
              <Card className="mt-3">
                <CardBody className="d-flex justify-content-center align-items-center min-height-300">
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : (
          <>
            <Row>
              <Col md="12">
                <Card className="mt-3">
                  <CardHeader>
                    <div className="d-flex align-items-center justify-content-end">
                      <Button
                        className="btn-deep-blue"
                        onClick={() => {
                          setCurrentQB(null);
                          setSelectedIndex(null);

                          setUiState((prev) => ({
                            ...prev,
                            qbPanel: {
                              isOpen: true,
                              mode: "ADD",
                            },
                          }));
                        }}
                      >
                        <i className="fa fa-plus mr-1 cursor" />
                        Question
                      </Button>

                      <Button
                        className="btn-deep-blue mr-5"
                        onClick={() =>
                          setUiState((prev) => ({
                            ...prev,
                            existingModal: true,
                          }))
                        }
                      >
                        <i className="fa fa-plus mr-1 cursor" />
                        Existing Questions
                      </Button>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                      {/* Left side - Filter Inputs + Search */}
                      <div className="d-flex flex-wrap align-items-center filter-group">
                        <Input
                          type="text"
                          placeholder="Search by question"
                          className="mr-2 mb-2 filter-input-large"
                          value={searchState.question}
                          onChange={(e) =>
                            setSearchState((prev) => ({
                              ...prev,
                              question: e.target.value,
                            }))
                          }
                          onKeyDown={handleKeyDown}
                        />

                        <Select
                          className="mr-2 mb-2 filter-select"
                          styles={fixedWidthCustomStyle()}
                          options={levelOptions}
                          placeholder="Select level"
                          value={searchState.level}
                          onChange={(selected) =>
                            setSearchState((prev) => ({
                              ...prev,
                              level: selected,
                            }))
                          }
                          onKeyDown={handleKeyDown}
                        />

                        <Select
                          className="mr-2 mb-2 filter-select"
                          styles={fixedWidthCustomStyle()}
                          options={QUESTION_LANGUAGE_OPTIONS}
                          placeholder="Select language"
                          value={searchState.language}
                          onChange={(selected) =>
                            setSearchState((prev) => ({
                              ...prev,
                              language: selected,
                            }))
                          }
                          onKeyDown={handleKeyDown}
                        />

                        <Select
                          className="mr-2 mb-2 filter-select"
                          options={ANSWER_TYPES}
                          placeholder="Select option type"
                          classNamePrefix="react-select"
                          styles={fixedWidthCustomStyle()}
                          value={searchState.ansType}
                          onChange={(selected) =>
                            setSearchState((prev) => ({
                              ...prev,
                              ansType: selected,
                            }))
                          }
                          onKeyDown={handleKeyDown}
                        />

                        <Button
                          color="default"
                          onClick={handleSearch}
                          disabled={loadingState.search}
                          className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${loadingState.search ? "search-btn" : ""}`}
                        >
                          {loadingState.search ? (
                            <>
                              <i className="fa fa-spinner fa-spin mr-1" />
                            </>
                          ) : (
                            <>
                              <i className="fa fa-search mr-1" />
                              Search
                            </>
                          )}
                        </Button>
                        {searchState.filterParams.length > 0 && (
                          <Button
                            color="default"
                            className="mb-2 filter-btn btn-deep-blue"
                            onClick={clearSearch}
                          >
                            <i className="fa fa-times mr-1" /> Clear
                          </Button>
                        )}
                      </div>

                      {/* <div className="d-flex align-items-center justify-content-end">
                    <Button className="btn-deep-blue">
                      <i className="fa fa-plus mr-1 cursor" />
                      Question
                    </Button>

                    <Button className="btn-deep-blue">
                      <i className="fa fa-plus mr-1 cursor" />
                      Existing Batch Question
                    </Button>
                  </div> */}
                    </div>

                    <div className="mt-2 mb-3 mx-1">
                      {questionBankInfo?.totalQuestions > 0 ? (
                        <>
                          {searchState.filterParams.length > 0 ? (
                            <span className="total-records">
                              Showing {questionBankInfo.totalFilteredQuestions}{" "}
                              of {questionBankInfo.totalQuestions} Records
                            </span>
                          ) : (
                            <span className="total-records">
                              Total Records : {questionBankInfo.totalQuestions}
                            </span>
                          )}
                        </>
                      ) : null}
                    </div>

                    {loadingState.table ? (
                      <div className="loaderContainer">
                        <i className="fa fa-spinner fa-spin fa-2xl loader" />
                      </div>
                    ) : loadingState.noRecords ? (
                      <div className="loaderContainer empty-style">
                        <div className="empty-icon">
                          <i className="fa-solid fa-circle-info"></i>
                        </div>
                        <span>No questions found.</span>
                      </div>
                    ) : (
                      <>
                        {qbQuestions.map((q, index) => renderQuestionCard(q, index))}

                        {Math.ceil(
                          questionBankInfo.totalFilteredQuestions /
                            (questionBankInfo.noOfRecordsPerPage ||
                              DEFAULT_RECORDS_PER_PAGE),
                        ) > 1 && (
                          <div className="mt-3">
                            <CustomPagination
                              totalPages={Math.ceil(
                                questionBankInfo.totalFilteredQuestions /
                                  (questionBankInfo.noOfRecordsPerPage ||
                                    DEFAULT_RECORDS_PER_PAGE),
                              )}
                              activePage={pageNum}
                              setPageNum={setPageNum}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
        <RightPanelCard
          isOpen={uiState.questionBankPanel}
          onClose={() =>
            setUiState((prev) => ({
              ...prev,
              questionBankPanel: false,
            }))
          }
        >
          <Card>
            <CardHeader tag="div" className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i
                    className="fa-solid fa-angle-left cursor mr-2"
                    onClick={() =>
                      setUiState((prev) => ({
                        ...prev,
                        questionBankPanel: false,
                      }))
                    }
                  />

                  <h4 className="mb-0 text-default section-title">
                    Edit Question Bank
                  </h4>
                </div>

                <i
                  className="fa-solid fa-xmark cursor text-muted"
                  onClick={() =>
                    setUiState((prev) => ({
                      ...prev,
                      questionBankPanel: false,
                    }))
                  }
                />
              </div>
            </CardHeader>

            <CardBody>
              <Row>
                <Col md="6" className="mb-3">
                  <FormGroup>
                    <Label className="small-label text-muted">
                      Question Bank Name
                      <span className="text-danger">*</span>
                    </Label>

                    <Input
                      name="name"
                      type="text"
                      placeholder="Enter question bank name"
                      maxLength={100}
                      value={editQuestionBank.name.value}
                      onChange={(e) =>
                        handleInputChange(e.target.name, e.target.value)
                      }
                      onBlur={(e) => handleOnBlur("name", e.target.value)}
                      className={
                        editQuestionBank.name.error ? "is-invalid" : ""
                      }
                    />

                    {editQuestionBank.name.error && (
                      <small className="invalid-feedback d-block">
                        {editQuestionBank.name.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-3">
                  <FormGroup>
                    <Label className="small-label text-muted">
                      Status <span className="text-danger"> *</span>
                    </Label>
                    <div className="radio-rapper mt-2">
                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            name="status"
                            value="ACTIVE"
                            className="cursor"
                            checked={editQuestionBank.status.value === "ACTIVE"}
                            onChange={(e) =>
                              handleInputChange("status", e.target.value)
                            }
                          />{" "}
                          Active
                        </Label>
                      </FormGroup>
                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            name="status"
                            value="INACTIVE"
                            className="cursor"
                            checked={
                              editQuestionBank.status.value === "INACTIVE"
                            }
                            onChange={(e) =>
                              handleInputChange("status", e.target.value)
                            }
                          />{" "}
                          Inactive
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>

            <CardFooter className="text-right">
              <Button
                color="secondary save-btn-sm"
                onClick={() =>
                  setUiState((prev) => ({
                    ...prev,
                    questionBankPanel: false,
                  }))
                }
              >
                Cancel
              </Button>

              <Button
                color="deep-blue save-btn-sm"
                onClick={handleUpdateQuestionBank}
                disabled={uiState.showLoaderOnClick}
              >
                {uiState.showLoaderOnClick ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-2" />
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </CardFooter>
          </Card>
        </RightPanelCard>
      </Container>
      <AddQuestion
        isOpen={uiState.qbPanel.isOpen}
        questionBankID={questionBankInfo.id}
        levelOptions={levelOptions}
        onClose={() =>
          setUiState((prev) => ({
            ...prev,
            qbPanel: { ...prev.qbPanel, isOpen: false },
          }))
        }
        mode={uiState.qbPanel.mode}
        selectedIndex={selectedIndex}
        initialData={currentQB}
        // negativeGrading={questionBankInfo.gradingType === "NEGATIVE"}
        assessmentLanguage={questionBankInfo.language}
        onSave={async () => {
          await fetchQBData(); // refetch list
        }}
      />
      <ExistingQuestionModal
        isOpen={uiState.existingModal}
        questionBankID={questionBankInfo.id}
        language={questionBankInfo.language}
        levelOptions={levelOptions}
        alreadyAddedQuestionIds={qbQuestions.map((q) => q.id)}
        toggleModal={() =>
          setUiState((prev) => ({ ...prev, existingModal: false }))
        }
        onSave={async () => {
          setUiState((prev) => ({ ...prev, existingModal: false }));
          await fetchQBData();
        }}
      />
      <Modal
        isOpen={!!previewData.src}
        toggle={() =>
          setPreviewData({
            src: "",
            width: 0,
            height: 0,
          })
        }
        size="xl"
        centered
      >
        <ModalHeader
          toggle={() =>
            setPreviewData({
              src: "",
              width: 0,
              height: 0,
            })
          }
        >
          Image Preview
        </ModalHeader>

        <ModalBody className="text-center">
          <img src={previewData.src} alt="Preview" className="preview-image" />
        </ModalBody>
      </Modal>
    </>
  );
};

export default QuestionBank;
